// src/pages/Donate.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const C = {
  bg:        "#0d0a05",
  bgCard:    "#12100a",
  border:    "rgba(212,168,83,0.12)",
  borderHov: "rgba(212,168,83,0.32)",
  gold:      "#d4a853",
  goldDim:   "rgba(212,168,83,0.5)",
  goldGhost: "var(--bone-text-muted)",
  ivory:     "#e8d5b0",
  ivoryDim:  "rgba(232,213,176,0.65)",
  ivoryGhost:"rgba(232,213,176,0.4)",
};

const DONATE_URL = "https://www.shopier.com/bonecastofficial/51503459";

const FONT_DISPLAY = "'Playfair Display',serif";
const FONT_BODY    = "'IM Fell English',serif";
const FONT_MONO    = "'Courier Prime',monospace";

/* ───────────────────────── Yardımcılar ───────────────────────── */

function useReveal(threshold = 0.1) {
  const ref = React.useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function GoldDivider({ maxWidth = 480, opacity = 0.25, margin = "0 0 40px" }) {
  return (
    <div style={{
      height: 1, maxWidth, margin,
      background: `linear-gradient(90deg,${C.gold},transparent)`,
      opacity,
    }} />
  );
}

function Ornament() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14,
      margin: "8px 0 8px", color: C.goldDim,
      fontFamily: FONT_MONO, fontSize: 10,
    }} aria-hidden="true">
      <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${C.border.replace("0.12", "0.4")})` }} />
      ✦
      <span style={{ flex: 1, height: 1, background: `linear-gradient(270deg,transparent,${C.border.replace("0.12", "0.4")})` }} />
    </div>
  );
}

/* ───────────────────────── Bileşenler ───────────────────────── */

function StatCard({ value, label, desc, delay = 0 }) {
  const [ref, visible] = useReveal(0.1);
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      className="bc-reveal"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: "1 1 200px", padding: "32px 24px 28px",
        background: "rgba(13, 10, 5, 0.65)",
        backdropFilter: "blur(8px)",
        border: `1px solid ${hov ? C.borderHov : "rgba(212, 168, 83, 0.2)"}`,
        borderRadius: 16,
        textAlign: "center", position: "relative",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s, border-color 0.3s ease`,
      }}
    >
      {/* üst altın çizgi: hover'da belirir */}
      <div style={{
        position: "absolute", top: -1, left: "20%", right: "20%", height: 1,
        background: C.gold, opacity: hov ? 0.8 : 0,
        transition: "opacity 0.3s ease",
      }} />
      <div style={{
        fontFamily: FONT_DISPLAY, fontSize: 40, fontWeight: 900,
        color: C.gold, lineHeight: 1, marginBottom: 10,
        textShadow: "0 0 30px rgba(212,168,83,0.3)",
      }}>{value}</div>
      <div style={{
        fontFamily: FONT_MONO, fontSize: 10, fontWeight: 700,
        letterSpacing: "0.22em", textTransform: "uppercase",
        color: C.ivory, marginBottom: 14,
      }}>{label}</div>
      <div style={{
        height: 1, width: 28, margin: "0 auto 14px",
        background: C.gold, opacity: 0.3,
      }} />
      <div style={{
        fontFamily: FONT_BODY, fontStyle: "italic",
        fontSize: 14, lineHeight: 1.7, color: C.ivoryDim,
      }}>{desc}</div>
    </div>
  );
}

function ManifestoPrinciple({ title, body, delay = 0 }) {
  const [ref, visible] = useReveal(0.15);
  return (
    <div
      ref={ref}
      className="bc-reveal"
      style={{
        textAlign: "left",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      <div>
        <h3 style={{
          fontFamily: FONT_DISPLAY, fontWeight: 700,
          fontSize: "clamp(19px,2.4vw,26px)", lineHeight: 1.3,
          color: C.gold, margin: "0 0 12px",
        }}>{title}</h3>
        <p style={{
          fontFamily: FONT_BODY, fontStyle: "italic",
          fontSize: "clamp(16px,1.8vw,19px)", lineHeight: 1.95,
          color: C.ivoryDim, margin: 0,
        }}>{body}</p>
      </div>
    </div>
  );
}

function SupportPillar({ icon, title, body }) {
  return (
    <div style={{
      flex: "1 1 170px", padding: "22px 18px",
      border: "1px solid rgba(212, 168, 83, 0.2)",
      borderRadius: 16,
      background: "rgba(13, 10, 5, 0.65)",
      backdropFilter: "blur(8px)",
      textAlign: "left",
    }}>
      <div style={{
        fontFamily: FONT_DISPLAY, fontSize: 22, color: C.gold,
        marginBottom: 10, lineHeight: 1,
      }} aria-hidden="true">{icon}</div>
      <div style={{
        fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 16,
        color: C.ivory, marginBottom: 8,
      }}>{title}</div>
      <div style={{
        fontFamily: FONT_MONO, fontSize: 11, lineHeight: 1.8,
        color: C.ivoryGhost, letterSpacing: "0.02em",
      }}>{body}</div>
    </div>
  );
}

/* ───────────────────────── Sayfa ───────────────────────── */

export default function Donate() {
  const { t } = useTranslation();
  const [btnHov, setBtnHov] = useState(false);
  const [refHero, vHero]   = useReveal(0.08);
  const [refQuote, vQuote] = useReveal(0.2);
  const [refCta, vCta]     = useReveal(0.08);

  useEffect(() => {
    if (!document.getElementById("bc-fonts-donate")) {
      const link = document.createElement("link");
      link.id = "bc-fonts-donate"; link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700;1,900&family=Courier+Prime:ital,wght@0,400;0,700;1,400&family=IM+Fell+English:ital@0;1&display=swap";
      document.head.appendChild(link);
    }
    window.scrollTo(0, 0);
  }, []);

  const PX = "clamp(24px,8vw,120px)";

  const stats = [
    { value: "∞",  label: t("donate.stats.s1.label"), desc: t("donate.stats.s1.desc") },
    { value: "3+", label: t("donate.stats.s2.label"), desc: t("donate.stats.s2.desc") },
    { value: "1",  label: t("donate.stats.s3.label"), desc: t("donate.stats.s3.desc") },
    { value: "0",  label: t("donate.stats.s4.label"), desc: t("donate.stats.s4.desc") },
  ];

  const principles = [
    { title: t("donate.principles.p1.title"), body: t("donate.principles.p1.body") },
    { title: t("donate.principles.p2.title"), body: t("donate.principles.p2.body") },
    { title: t("donate.principles.p3.title"), body: t("donate.principles.p3.body") },
  ];

  const pillars = [
    { icon: "◈", title: t("donate.support.pillar1.title"), body: t("donate.support.pillar1.body") },
    { icon: "✦", title: t("donate.support.pillar2.title"), body: t("donate.support.pillar2.body") },
    { icon: "∅", title: t("donate.support.pillar3.title"), body: t("donate.support.pillar3.body") },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.ivory }}>

      {/* ── Geri butonu ── */}
      <div style={{ padding: `24px ${PX} 0` }}>
        <Link to="/" className="bc-link" style={{
          fontFamily: FONT_MONO, fontSize: 11,
          color: C.gold, textDecoration: "none", letterSpacing: "0.15em",
          display: "inline-flex", alignItems: "center", gap: 6,
        }}>
          ← {t("common.back")}
        </Link>
      </div>

      {/* ── 1. Hero: manifesto girişi ── */}
      <section ref={refHero} style={{ padding: `64px ${PX} 0` }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <p style={{
            fontFamily: FONT_MONO, fontSize: 10,
            letterSpacing: "0.38em", color: C.goldGhost,
            textTransform: "uppercase", marginBottom: 20,
            opacity: vHero ? 1 : 0, transition: "opacity 0.6s ease",
          }}>
            ✦ {t("donate.eyebrow")} ✦
          </p>

          <h1 className="bc-reveal" style={{
            fontFamily: FONT_DISPLAY,
            fontSize: "clamp(38px,7vw,84px)", fontWeight: 900,
            color: C.gold, margin: "0 0 28px", lineHeight: 1.02,
            letterSpacing: "-0.02em",
            opacity: vHero ? 1 : 0,
            transform: vHero ? "translateY(0)" : "translateY(24px)",
            transition: "opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s",
            textShadow: "0 0 60px rgba(212,168,83,0.15)",
          }}>
            {t("donate.title")}
          </h1>

          <p className="bc-reveal" style={{
            fontFamily: FONT_BODY, fontStyle: "italic",
            fontSize: "clamp(18px,2.4vw,26px)", lineHeight: 1.7,
            color: C.ivory, maxWidth: 640, margin: "0 0 32px",
            opacity: vHero ? 1 : 0,
            transition: "opacity 0.9s ease 0.35s",
          }}>
            {t("donate.subtitle")}
          </p>

          <GoldDivider />

          <p className="bc-reveal" style={{
            fontFamily: FONT_MONO, fontSize: 12.5, lineHeight: 2,
            color: C.ivoryGhost, letterSpacing: "0.04em",
            maxWidth: 560, margin: "0 0 72px",
            opacity: vHero ? 1 : 0,
            transition: "opacity 0.9s ease 0.6s",
          }}>
            {t("donate.manifesto")}
          </p>
        </div>
      </section>

      {/* ── 2. Metrik kartları ── */}
      <section style={{ padding: `0 ${PX} 96px` }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {stats.map((s, i) => (
              <StatCard key={i} value={s.value} label={s.label} desc={s.desc} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Manifesto / felsefe ── */}
      <section style={{ padding: `0 ${PX} 40px` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          {principles.map((p, i) => (
            <React.Fragment key={i}>
              <ManifestoPrinciple {...p} delay={0.05} />
              {i < principles.length - 1 && (
                <div style={{ margin: "44px 0" }}><Ornament /></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── Alıntı bloğu ── */}
      <section ref={refQuote} style={{ padding: `40px ${PX} 96px` }}>
        <div className="bc-reveal" style={{
          maxWidth: 720, margin: "0 auto",
          padding: "32px clamp(20px,4vw,40px)",
          border: "1px solid rgba(212, 168, 83, 0.2)",
          borderRadius: 16,
          background: "rgba(13, 10, 5, 0.65)",
          backdropFilter: "blur(8px)",
          opacity: vQuote ? 1 : 0,
          transform: vQuote ? "translateX(0)" : "translateX(-16px)",
          transition: "opacity 0.8s ease, transform 0.8s ease",
        }}>
          <p style={{
            fontFamily: FONT_DISPLAY, fontStyle: "italic",
            fontSize: "clamp(20px,2.6vw,30px)", lineHeight: 1.55,
            color: C.gold, margin: 0, letterSpacing: "0.01em",
          }}>
            “{t("donate.quote")}”
          </p>
        </div>
      </section>

      {/* ── 4. Destek kartı ── */}
      <section ref={refCta} style={{ padding: `0 ${PX} 110px` }}>
        <div className="bc-reveal" style={{
          maxWidth: 760, margin: "0 auto",
          background: "rgba(13, 10, 5, 0.65)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(212, 168, 83, 0.2)",
          borderRadius: 16,
          padding: "clamp(32px,5vw,56px) clamp(22px,5vw,48px)",
          textAlign: "center",
          opacity: vCta ? 1 : 0,
          transform: vCta ? "translateY(0)" : "translateY(24px)",
          transition: "opacity 0.8s ease, transform 0.8s ease",
          position: "relative", overflow: "hidden",
        }}>
          {/* Arka plan parıltısı */}
          <div style={{
            position: "absolute", top: -70, left: "50%", transform: "translateX(-50%)",
            width: 360, height: 140,
            background: "radial-gradient(ellipse,rgba(212,168,83,0.09) 0%,transparent 70%)",
            pointerEvents: "none",
          }} />

          <p style={{
            fontFamily: FONT_MONO, fontSize: 10,
            letterSpacing: "0.35em", color: C.goldGhost,
            textTransform: "uppercase", marginBottom: 18, position: "relative",
          }}>
            ✦ {t("donate.support.eyebrow")} ✦
          </p>
          <h2 style={{
            fontFamily: FONT_DISPLAY, fontSize: "clamp(24px,3.4vw,36px)",
            fontWeight: 700, color: C.gold, margin: "0 0 18px", position: "relative",
          }}>
            {t("donate.support.title")}
          </h2>
          <p style={{
            fontFamily: FONT_BODY, fontStyle: "italic",
            fontSize: "clamp(16px,1.8vw,18px)", lineHeight: 1.85,
            color: C.ivoryDim, maxWidth: 520, margin: "0 auto 32px",
            position: "relative",
          }}>
            {t("donate.support.intro")}
          </p>

          <div style={{
            display: "flex", gap: 12, flexWrap: "wrap",
            marginBottom: 40, position: "relative",
          }}>
            {pillars.map((p, i) => <SupportPillar key={i} {...p} />)}
          </div>

          {/* Destek butonu */}
          <a
            href={DONATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bc-link"
            onMouseEnter={() => setBtnHov(true)}
            onMouseLeave={() => setBtnHov(false)}
            style={{
              display: "inline-block",
              padding: "16px 52px",
              background: btnHov ? "#c49840" : C.gold,
              textDecoration: "none", cursor: "pointer",
              fontFamily: FONT_MONO, fontWeight: "bold",
              fontSize: 12, letterSpacing: "0.28em", textTransform: "uppercase",
              color: "#0d0a05",
              transition: "background 0.25s ease, box-shadow 0.25s ease",
              boxShadow: btnHov ? "0 0 32px rgba(212,168,83,0.4)" : "none",
              position: "relative",
            }}
          >
            ♥ {t("donate.support.button")}
          </a>

          <p style={{
            fontFamily: FONT_MONO, fontSize: 10,
            color: C.ivoryGhost, letterSpacing: "0.12em",
            margin: "18px 0 0", position: "relative",
          }}>
            {t("donate.support.secure")}
          </p>
        </div>
      </section>

      <style>{`
        * { box-sizing: border-box; }
        ::selection { background: rgba(212,168,83,0.2); color: #e8d5b0; }
        .bc-link:focus-visible { outline: 2px solid #d4a853; outline-offset: 4px; }
        @media (prefers-reduced-motion: reduce) {
          .bc-reveal { transition: none !important; transform: none !important; }
        }
      `}</style>
    </div>
  );
}
